import React, { useEffect, useState } from 'react';
import { Settings as SettingsIcon, User, Bell, Shield, Palette, Save } from 'lucide-react';
import './Settings.css';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [shopStatus, setShopStatus] = useState({ is_open: false, last_opened_at: null, last_first_order_at: null });
  const [settings, setSettings] = useState({
    profile: {
      shopName: '',
      ownerName: '',
      email: '',
      phone: '',
      shopAddress: '',
      logo_url: '',
      banner_url: ''
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      orderUpdates: true,
      vendorUpdates: true,
      systemAlerts: true,
      weeklyReports: false
    },
    security: {
      twoFactorAuth: false,
      sessionTimeout: '30',
      passwordExpiry: '90',
      loginAlerts: true
    },
    appearance: {
      theme: 'light',
      sidebarCollapsed: false,
      compactMode: false
    }
  });

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'appearance', label: 'Appearance', icon: Palette }
  ];

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const handleUpload = async (type, file) => {
    if (!file) return;
    try {
      const token = localStorage.getItem('token');
      const form = new FormData();
      if (type === 'logo') form.append('logo', file);
      if (type === 'banner') form.append('banner', file);
      const res = await fetch('/api/vendor/profile/media', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: form
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Upload failed');
      } else {
        setSettings(prev => ({
          ...prev,
          profile: {
            ...prev.profile,
            logo_url: data.logo_url !== undefined ? data.logo_url : prev.profile.logo_url,
            banner_url: data.banner_url !== undefined ? data.banner_url : prev.profile.banner_url
          }
        }));
      }
    } catch (e) { console.error('Media upload error:', e); }
  };

  const removeMedia = async (type) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/vendor/profile/media?type=${encodeURIComponent(type)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Remove failed');
      } else {
        setSettings(prev => ({
          ...prev,
          profile: {
            ...prev.profile,
            logo_url: type === 'logo' ? '' : prev.profile.logo_url,
            banner_url: type === 'banner' ? '' : prev.profile.banner_url
          }
        }));
      }
    } catch (e) { console.error('Remove media error:', e); }
  };

  const loadVendorProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/vendor/profile', { headers: { 'Authorization': `Bearer ${token}` }});
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({
          ...prev,
          profile: {
            shopName: data.shopName || '',
            ownerName: data.ownerName || '',
            email: data.email || '',
            phone: data.phone || '',
            shopAddress: data.shopAddress || '',
            logo_url: data.logo_url || '',
            banner_url: data.banner_url || ''
          }
        }));
        // Initialize meta
        setHoursState(data.hours || defaultHours);
        setSocialState({
          website: data.social?.website || '',
          instagram: data.social?.instagram || '',
          facebook: data.social?.facebook || '',
          twitter: data.social?.twitter || ''
        });
      }
    } catch (e) { console.error('Load profile error:', e); }
  };

  const loadShopStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/vendor/shop-status', { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setShopStatus({
          is_open: !!data.is_open,
          last_opened_at: data.last_opened_at || null,
          last_first_order_at: data.last_first_order_at || null
        });
      }
    } catch (e) { console.error('Load shop status error:', e); }
  };

  useEffect(() => { loadVendorProfile(); loadShopStatus(); }, []);

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/vendor/profile', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shopName: settings.profile.shopName,
          ownerName: settings.profile.ownerName,
          email: settings.profile.email,
          phone: settings.profile.phone,
          shopAddress: settings.profile.shopAddress
        })
      });
      if (!res.ok) {
        const e = await res.json();
        alert(e.error || 'Failed to save profile');
      } else {
        await loadVendorProfile();
      }
    } catch (e) { console.error('Save profile error:', e); }
  };

  const defaultHours = {
    mon: { open: '09:00', close: '21:00', closed: false },
    tue: { open: '09:00', close: '21:00', closed: false },
    wed: { open: '09:00', close: '21:00', closed: false },
    thu: { open: '09:00', close: '21:00', closed: false },
    fri: { open: '09:00', close: '21:00', closed: false },
    sat: { open: '09:00', close: '21:00', closed: false },
    sun: { open: '09:00', close: '21:00', closed: true }
  };
  const [hoursState, setHoursState] = useState(defaultHours);
  const [socialState, setSocialState] = useState({ website: '', instagram: '', facebook: '', twitter: '' });

  const saveMeta = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/vendor/profile/meta', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ hours: hoursState, social: socialState })
      });
      if (!res.ok) {
        const e = await res.json();
        alert(e.error || 'Failed to save profile meta');
      }
    } catch (e) { console.error('Save meta error:', e); }
  };

  const renderProfileSettings = () => (
    <div className="settings-section">
      <h3>Shop Status</h3>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Shop is {shopStatus.is_open ? 'Open' : 'Closed'}</label>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={shopStatus.is_open}
              onChange={async (e) => {
                const next = e.target.checked;
                try {
                  const token = localStorage.getItem('token');
                  const res = await fetch('/api/vendor/shop-status', {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ is_open: next })
                  });
                  if (res.ok) {
                    setShopStatus(prev => ({ ...prev, is_open: next, last_first_order_at: next ? null : prev.last_first_order_at }));
                  } else {
                    const err = await res.json().catch(() => ({}));
                    alert(err.error || 'Failed to update shop status');
                  }
                } catch (err) {
                  console.error('Update shop status error:', err);
                }
              }}
            />
            <span className="toggle-slider"></span>
          </label>
          <div style={{ marginTop: 8, color: '#666' }}>
            <div>Last opened: {shopStatus.last_opened_at ? new Date(shopStatus.last_opened_at).toLocaleString() : '—'}</div>
            <div>First order after last open: {shopStatus.last_first_order_at ? new Date(shopStatus.last_first_order_at).toLocaleString() : '—'}</div>
          </div>
        </div>
      </div>

      <h3>Shop & Owner Details</h3>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Shop Name</label>
          <input
            type="text"
            className="form-input"
            value={settings.profile.shopName}
            onChange={(e) => handleSettingChange('profile', 'shopName', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Owner Name</label>
          <input
            type="text"
            className="form-input"
            value={settings.profile.ownerName}
            onChange={(e) => handleSettingChange('profile', 'ownerName', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="form-input"
            value={settings.profile.email}
            onChange={(e) => handleSettingChange('profile', 'email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Phone Number</label>
          <input
            type="tel"
            className="form-input"
            value={settings.profile.phone}
            onChange={(e) => handleSettingChange('profile', 'phone', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Shop Address</label>
          <input
            type="text"
            className="form-input"
            value={settings.profile.shopAddress}
            onChange={(e) => handleSettingChange('profile', 'shopAddress', e.target.value)}
          />
        </div>
      </div>

      <h3 style={{ marginTop: 20 }}>Branding</h3>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Shop Logo</label>
          {settings.profile.logo_url && (
            <div style={{ marginBottom: 8 }}>
              <img src={settings.profile.logo_url} alt="logo" style={{ height: 48 }} />
            </div>
          )}
          <input type="file" accept="image/*" onChange={(e) => handleUpload('logo', e.target.files?.[0])} />
        </div>
        <div className="form-group">
          <label className="form-label">Shop Banner</label>
          {settings.profile.banner_url && (
            <div style={{ marginBottom: 8 }}>
              <img src={settings.profile.banner_url} alt="banner" style={{ height: 80 }} />
            </div>
          )}
          <input type="file" accept="image/*" onChange={(e) => handleUpload('banner', e.target.files?.[0])} />
        </div>
      </div>

      <h3 style={{ marginTop: 20 }}>Operating Hours</h3>
      <div className="form-grid">
        {Object.entries(hoursState).map(([day, val]) => (
          <div className="form-group" key={day}>
            <label className="form-label" style={{ textTransform: 'capitalize' }}>{day}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={val.closed} onChange={(e) => setHoursState(prev => ({ ...prev, [day]: { ...prev[day], closed: e.target.checked } }))} /> Closed
              </label>
              {!val.closed && (
                <>
                  <input type="time" value={val.open} onChange={(e) => setHoursState(prev => ({ ...prev, [day]: { ...prev[day], open: e.target.value } }))} />
                  <span>to</span>
                  <input type="time" value={val.close} onChange={(e) => setHoursState(prev => ({ ...prev, [day]: { ...prev[day], close: e.target.value } }))} />
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <h3 style={{ marginTop: 20 }}>Social Links</h3>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Website</label>
          <input type="url" className="form-input" value={socialState.website} onChange={(e) => setSocialState(prev => ({ ...prev, website: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">Instagram</label>
          <input type="url" className="form-input" value={socialState.instagram} onChange={(e) => setSocialState(prev => ({ ...prev, instagram: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">Facebook</label>
          <input type="url" className="form-input" value={socialState.facebook} onChange={(e) => setSocialState(prev => ({ ...prev, facebook: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">Twitter</label>
          <input type="url" className="form-input" value={socialState.twitter} onChange={(e) => setSocialState(prev => ({ ...prev, twitter: e.target.value }))} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <button className="btn btn-primary" type="button" onClick={saveMeta}>Save Hours & Social</button>
        <button className="btn btn-secondary" type="button" onClick={() => removeMedia('logo')}>Remove Logo</button>
        <button className="btn btn-secondary" type="button" onClick={() => removeMedia('banner')}>Remove Banner</button>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="settings-section">
      <h3>Notification Preferences</h3>
      <div className="notification-settings">
        {Object.entries(settings.notifications).map(([key, value]) => (
          <div key={key} className="notification-item">
            <div className="notification-info">
              <h4>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</h4>
              <p>Receive notifications for {key.toLowerCase().replace(/([A-Z])/g, ' $1')}</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={value}
                onChange={(e) => handleSettingChange('notifications', key, e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="settings-section">
      <h3>Security Settings</h3>
      <div className="security-settings">
        <div className="security-item">
          <div className="security-info">
            <h4>Two-Factor Authentication</h4>
            <p>Add an extra layer of security to your account</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={settings.security.twoFactorAuth}
              onChange={(e) => handleSettingChange('security', 'twoFactorAuth', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
        
        <div className="form-group">
          <label className="form-label">Session Timeout (minutes)</label>
          <select
            className="form-input"
            value={settings.security.sessionTimeout}
            onChange={(e) => handleSettingChange('security', 'sessionTimeout', e.target.value)}
          >
            <option value="15">15 minutes</option>
            <option value="30">30 minutes</option>
            <option value="60">1 hour</option>
            <option value="120">2 hours</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Password Expiry (days)</label>
          <select
            className="form-input"
            value={settings.security.passwordExpiry}
            onChange={(e) => handleSettingChange('security', 'passwordExpiry', e.target.value)}
          >
            <option value="30">30 days</option>
            <option value="60">60 days</option>
            <option value="90">90 days</option>
            <option value="180">180 days</option>
          </select>
        </div>
        
        <div className="security-item">
          <div className="security-info">
            <h4>Login Alerts</h4>
            <p>Get notified when someone logs into your account</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={settings.security.loginAlerts}
              onChange={(e) => handleSettingChange('security', 'loginAlerts', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="settings-section">
      <h3>Appearance Settings</h3>
      <div className="appearance-settings">
        <div className="form-group">
          <label className="form-label">Theme</label>
          <div className="theme-options">
            <label className="theme-option">
              <input
                type="radio"
                name="theme"
                value="light"
                checked={settings.appearance.theme === 'light'}
                onChange={(e) => handleSettingChange('appearance', 'theme', e.target.value)}
              />
              <span>Light</span>
            </label>
            <label className="theme-option">
              <input
                type="radio"
                name="theme"
                value="dark"
                checked={settings.appearance.theme === 'dark'}
                onChange={(e) => handleSettingChange('appearance', 'theme', e.target.value)}
              />
              <span>Dark</span>
            </label>
            <label className="theme-option">
              <input
                type="radio"
                name="theme"
                value="auto"
                checked={settings.appearance.theme === 'auto'}
                onChange={(e) => handleSettingChange('appearance', 'theme', e.target.value)}
              />
              <span>Auto</span>
            </label>
          </div>
        </div>
        
        <div className="appearance-item">
          <div className="appearance-info">
            <h4>Compact Mode</h4>
            <p>Use a more compact layout with smaller spacing</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={settings.appearance.compactMode}
              onChange={(e) => handleSettingChange('appearance', 'compactMode', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'security':
        return renderSecuritySettings();
      case 'appearance':
        return renderAppearanceSettings();
      default:
        return renderProfileSettings();
    }
  };

  return (
    <div className="settings">
      <div className="settings-header">
        <div className="header-content">
          <h1>Settings</h1>
          <p>Manage your shop profile, preferences, and appearance</p>
        </div>
        <button className="btn btn-primary" onClick={handleSave}>
          <Save size={20} />
          Save Changes
        </button>
      </div>

      <div className="settings-container">
        <div className="settings-sidebar">
          <nav className="settings-nav">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon size={20} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="settings-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Settings;
